import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "./auth-context";
import { Wordmark } from "./wordmark";
import "./tenure-landing.css";
import { TenureEducationSections } from "./tenure-education";
import { TenureProductStory } from "./tenure-product-story";
import { TenureCampusDecisions } from "./tenure-campus-decisions";

let capturedFilmTime = 0;
export function captureTenureIntro(): void {
  const film = document.querySelector<HTMLVideoElement>(".tenure-loading-film");
  const time = film?.currentTime;
  if (typeof time === "number" && Number.isFinite(time) && time >= 0)
    capturedFilmTime = time;
}
type View = "intro" | "landing" | "login";
type Perspective = "operator" | "developer" | "whatif";
const copy: Record<
  Perspective,
  { title: string; body: string; labels: string[] }
> = {
  operator: {
    title: "A campus has to work as a school.",
    body: "Test enrolment, fees and operating cash flow alongside rent and incentives. See where the opening years put pressure on affordability.",
    labels: [
      "Rent & escalation",
      "Operating cash flow",
      "Long-term affordability",
    ],
  },
  developer: {
    title: "Understand what makes the project viable.",
    body: "Examine development costs, funding and return assumptions alongside the proposed lease. Bring informed questions to a constructive discussion.",
    labels: ["Development costs", "Funding structure", "Project returns"],
  },
  whatif: {
    title: "Put the assumptions under pressure.",
    body: "Compare slower enrolment, changed costs and different lease assumptions. Find the trade-offs worth discussing with your developer and advisers.",
    labels: ["Rent assumptions", "Cost changes", "Scenario comparison"],
  },
};
const seen = () => {
  try {
    return sessionStorage.getItem("tenure-intro-seen") === "1";
  } catch {
    return false;
  }
};
const markSeen = () => {
  try {
    sessionStorage.setItem("tenure-intro-seen", "1");
  } catch {
    /* optional */
  }
};
function isPublicRoot(): boolean {
  const { pathname, hash, search } = window.location;
  return (
    (pathname === "/" || pathname === "/index.html") &&
    ["", "#", "#/"].includes(hash) &&
    !new URLSearchParams(search).has("code") &&
    !new URLSearchParams(search).has("error")
  );
}

function Intro({
  onExit,
  onSignIn,
}: {
  onExit: () => void;
  onSignIn: () => void;
}): React.JSX.Element {
  const ref = useRef<HTMLVideoElement>(null);
  const timers = useRef<number[]>([]);
  const didExit = useRef(false);
  const [exiting, setExiting] = useState(false);
  const exit = (pause = false) => {
    if (didExit.current) return;
    didExit.current = true;
    if (pause) ref.current?.pause();
    markSeen();
    setExiting(true);
    timers.current.push(window.setTimeout(onExit, 650));
  };
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    let active = true;
    let played = false;
    const playing = () => {
      played = true;
    };
    const metadata = () => {
      if (capturedFilmTime > 0 && Number.isFinite(video.duration))
        video.currentTime = Math.min(
          capturedFilmTime,
          Math.max(0, video.duration - 0.15),
        );
      capturedFilmTime = 0;
    };
    const ended = () => exit();
    const noStart = window.setTimeout(() => {
      if (!played && video.currentTime <= 0) exit();
    }, 5000);
    const safety = window.setTimeout(() => exit(), 20000);
    timers.current = [noStart, safety];
    video.addEventListener("playing", playing);
    video.addEventListener("loadedmetadata", metadata);
    video.addEventListener("ended", ended);
    video.addEventListener("error", ended);
    Promise.resolve(video.play()).catch(() => {
      if (active) exit();
    });
    return () => {
      active = false;
      timers.current.forEach(window.clearTimeout);
      video.pause();
      video.removeEventListener("playing", playing);
      video.removeEventListener("loadedmetadata", metadata);
      video.removeEventListener("ended", ended);
      video.removeEventListener("error", ended);
    };
  }, []);
  return (
    <main
      className={`tenure-intro${exiting ? " is-exiting" : ""}`}
      aria-label="TENURE introduction"
    >
      <div className="tenure-intro-monument">
        <Wordmark size="lg" />
        <video
          ref={ref}
          className="tenure-loading-film"
          muted
          playsInline
          autoPlay
          poster="/assets/brand/tenure-enduring-poster.jpg"
          src="/assets/brand/tenure-enduring.mp4"
        />
      </div>
      <p className="tenure-intro-caption">
        Analysis built to stand the test of time.
      </p>
      <div className="tenure-intro-actions">
        <button type="button" onClick={() => exit(true)}>
          Skip film
        </button>
        <button
          type="button"
          onClick={() => {
            if (didExit.current) return;
            didExit.current = true;
            ref.current?.pause();
            markSeen();
            onSignIn();
          }}
        >
          Sign in <span aria-hidden="true">↗</span>
        </button>
      </div>
    </main>
  );
}
function Schematic({
  perspective,
}: {
  perspective: Perspective;
}): React.JSX.Element {
  const labels = copy[perspective].labels;
  return (
    <div className="tenure-schematic">
      <svg viewBox="0 0 720 145" aria-hidden="true">
        <path d="M20 113H700M40 88L182 63 340 86 486 30 672 54" />
        <path d="M40 88V113M182 63V113M340 86V113M486 30V113M672 54V113" />
      </svg>
      <div className="tenure-schematic-labels">
        {labels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <span className="tenure-schematic-note">Illustrative view</span>
    </div>
  );
}
function Landing({
  onLogin,
  onReplay,
}: {
  onLogin: () => void;
  onReplay: () => void;
}): React.JSX.Element {
  const [perspective, setPerspective] = useState<Perspective>("operator");
  const root = useRef<HTMLElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    const prior = document.title;
    document.title =
      "TENURE — Financial modelling for education campus decisions";
    return () => {
      if (
        document.title ===
        "TENURE — Financial modelling for education campus decisions"
      )
        document.title = prior;
    };
  }, []);
  useEffect(() => {
    if (!root.current || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        }),
      { threshold: 0.12 },
    );
    root.current
      .querySelectorAll(".tenure-reveal")
      .forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);
  const jump = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    root.current
      ?.querySelector(`#${id}`)
      ?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <main ref={root} className="tenure-landing" aria-label="TENURE">
      <header className="tenure-header">
        <Wordmark size="sm" />
        <nav aria-label="Primary">
          <a href="#perspective" onClick={jump("perspective")}>
            The perspective
          </a>
          <a href="#workspace" onClick={jump("workspace")}>
            The workspace
          </a>
          <a href="#resources" onClick={jump("resources")}>
            Resources
          </a>
        </nav>
        <button className="tenure-arrow-button" type="button" onClick={onLogin}>
          Sign in <span aria-hidden="true">↗</span>
        </button>
      </header>
      <section className="tenure-hero tenure-reveal">
        <div className="tenure-hero-copy">
          <p className="tenure-eyebrow">FOR EDUCATION OPERATORS</p>
          <h1 ref={heading} tabIndex={-1}>
            Negotiate on <em>facts.</em>
          </h1>
          <p className="tenure-lede">
            TENURE is financial modelling for education operators negotiating
            purpose-built campuses. It connects development costs, lease terms,
            enrolment and operating cash flow, with construction benchmarks and
            reports for advisers and boards.
          </p>
          <div className="tenure-cta-row">
            <a
              className="tenure-button"
              href="mailto:kyle@stratosfyre.com.au?subject=TENURE%20walkthrough"
            >
              Request a walkthrough <span aria-hidden="true">↗</span>
            </a>
            <a
              className="tenure-text-link"
              href="/resources/lease-horizon-check.html"
            >
              Try the free lease check <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
        <figure className="tenure-monument">
          <div className="tenure-image-frame">
            <img
              src="/assets/brand/tenure-landing-monument.jpg"
              alt="Sandstone and brass TENURE letters standing before a weathered city"
            />
          </div>
          <figcaption>A changing world. A considered position.</figcaption>
        </figure>
      </section>
      <section className="tenure-facts tenure-reveal">
        <p>LONG-TERM OCCUPANCY, MODELLED.</p>
        <div>
          <span>Operator economics</span>
          <span>Developer returns</span>
          <span>Board reporting</span>
        </div>
      </section>
      <section id="perspective" className="tenure-perspective tenure-reveal">
        <p className="tenure-eyebrow">THE PERSPECTIVE</p>
        <h2>Facts, not feelings, at the negotiating table.</h2>
        <p className="tenure-perspective-introduction">
          You need a campus the school can sustain. Your developer needs a
          viable project. Bring costs, benchmarks and operating assumptions into
          the same discussion, so you can challenge the proposal constructively
          and identify the terms worth negotiating.
        </p>
        <div className="tenure-perspective-grid">
          <div
            className="tenure-perspective-controls"
            role="group"
            aria-label="Perspective"
          >
            {(["operator", "developer", "whatif"] as Perspective[]).map(
              (item) => (
                <button
                  key={item}
                  aria-pressed={perspective === item}
                  onClick={() => setPerspective(item)}
                  type="button"
                >
                  {item === "whatif"
                    ? "What if"
                    : item[0].toUpperCase() + item.slice(1)}
                </button>
              ),
            )}
          </div>
          <div className="tenure-perspective-copy" key={perspective}>
            <h3>{copy[perspective].title}</h3>
            <p>{copy[perspective].body}</p>
            <Schematic perspective={perspective} />
          </div>
        </div>
      </section>
      <TenureProductStory />
      <TenureCampusDecisions />
      <section id="workspace" className="tenure-workspace tenure-reveal">
        <p className="tenure-eyebrow">THE WORKSPACE</p>
        <h2>From the working model to the board table.</h2>
        <div className="tenure-steps">
          <article>
            <b>01</b>
            <h3>Model the proposal</h3>
            <p>
              Build the live cost, lease and funding view as assumptions change.
            </p>
          </article>
          <article>
            <b>02</b>
            <h3>Test the alternatives</h3>
            <p>
              Compare scenarios and sensitivities while the decision is still
              open.
            </p>
          </article>
          <article>
            <b>03</b>
            <h3>Carry the reasoning forward</h3>
            <p>Export clear Excel and PDF views that keep the logic intact.</p>
          </article>
        </div>
        <div className="tenure-final">
          <h2>Bring your next campus decision into focus.</h2>
          <a
            className="tenure-button"
            href="mailto:kyle@stratosfyre.com.au?subject=TENURE%20walkthrough"
          >
            Request a walkthrough <span aria-hidden="true">↗</span>
          </a>
          <p>
            A conversation about your project and whether TENURE fits. Already a
            member?{" "}
            <button
              className="tenure-member-link"
              type="button"
              onClick={onLogin}
            >
              Sign in
            </button>
          </p>
        </div>
      </section>
      <TenureEducationSections />
      <footer className="tenure-footer">
        <span>TENURE</span>
        <span>Analysis built to stand the test of time.</span>
        <button type="button" onClick={onReplay}>
          Replay film
        </button>
      </footer>
    </main>
  );
}
export function TenureEntrance({
  children,
}: Readonly<{ children: React.ReactNode }>): React.JSX.Element {
  const auth = useAuth();
  const [routeBypass, setRouteBypass] = useState(() => !isPublicRoot());
  const [view, setView] = useState<View>(() => (seen() ? "landing" : "intro"));
  useEffect(() => {
    const controller = new AbortController();
    const sync = () => setRouteBypass(!isPublicRoot());
    window.addEventListener("hashchange", sync, { signal: controller.signal });
    window.addEventListener("popstate", sync, { signal: controller.signal });
    return () => controller.abort();
  }, []);
  if (
    auth.authed ||
    auth.loading ||
    auth.productionAuthUnavailable ||
    routeBypass
  )
    return <>{children}</>;
  if (view === "login")
    return (
      <div
        className="tenure-login-shell"
        onKeyDownCapture={(event) => {
          if (event.key === "Escape") setView("landing");
        }}
      >
        <button
          className="tenure-back"
          type="button"
          aria-label="Back to TENURE"
          aria-keyshortcuts="Escape"
          onClick={() => setView("landing")}
        >
          <span aria-hidden="true">← </span>Back to TENURE <small>Esc</small>
        </button>
        {children}
      </div>
    );
  if (view === "intro")
    return (
      <Intro
        onExit={() => setView("landing")}
        onSignIn={() => setView("login")}
      />
    );
  return (
    <Landing
      onLogin={() => setView("login")}
      onReplay={() => {
        capturedFilmTime = 0;
        setView("intro");
      }}
    />
  );
}
